import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/Modal';
import { useSocket } from '../contexts/SocketContext';
import TabBar from '../components/TabBar';
import api from '../utils/api';
import { formatBJTime } from '../utils/datetime';
import { copyToClipboard } from '../utils/clipboard';
import {
  ArrowLeft, Video, Copy, Upload, ImageOff, Loader,
  CheckCircle2, XCircle, Clock, RefreshCw, ChevronDown,
} from 'lucide-react';

// ── 状态映射 ─────────────────────────────────────────────────────────────────
const STATUS = {
  pending  : { label: '待录制', color: 'var(--c-text-secondary)', bg: 'var(--c-bg)', Icon: Clock },
  submitted: { label: '审核中', color: '#ff9f0a',                 bg: 'rgba(255,159,10,0.13)', Icon: Loader },
  approved : { label: '已通过', color: 'var(--c-success)',        bg: 'rgba(52,199,89,0.13)', Icon: CheckCircle2 },
  rejected : { label: '未通过', color: 'var(--c-danger)',         bg: 'rgba(255,59,48,0.11)', Icon: XCircle },
};

// ── 任务卡片 ─────────────────────────────────────────────────────────────────
function TaskCard({ task, onUpload }) {
  const { showToast } = useToast();
  const s     = STATUS[task.status] || STATUS.pending;
  const SIcon = s.Icon;
  const [refOpen, setRefOpen] = useState(false); // 参考截图折叠展开状态

  return (
    <div className="card" style={{ marginBottom: 12, padding: 0, overflow: 'hidden' }}>
      {/* 卡头：号码 + 状态 */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 14px', borderBottom: '1px solid var(--c-divider)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Video size={15} color="var(--c-primary)" />
          <span style={{
            fontSize: 12, fontWeight: 700, borderRadius: 6, padding: '2px 8px',
            background: task.business === 'puzzle' ? 'rgba(175,82,222,0.14)' : 'rgba(0,122,255,0.12)',
            color: task.business === 'puzzle' ? '#8944ab' : 'var(--c-primary)',
          }}>{task.business_label || '排五'}</span>
          <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: '0.04em' }}>
            {task.phone}
          </span>
          <button
            onClick={async () => { await copyToClipboard(task.phone); showToast('已复制号码', 'success'); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}
            title="复制号码">
            <Copy size={13} color="var(--c-text-tertiary)" />
          </button>
        </div>
        <span style={{
          display: 'flex', alignItems: 'center', gap: 4,
          fontSize: 12, fontWeight: 600, color: s.color,
          background: s.bg, borderRadius: 8, padding: '3px 9px',
        }}>
          <SIcon size={12} strokeWidth={2.2} />
          {s.label}
        </span>
      </div>

      {/* 来源：这个号码是哪个业务、哪一批 / 哪个数据包的 */}
      {task.source_detail && (
        <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--c-text-secondary)', borderBottom: '1px solid var(--c-divider)', background: 'var(--c-bg)' }}>
          {task.source_detail}
        </div>
      )}

      {/* 参考截图：有图可折叠展开，无图仅显示号码 */}
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--c-divider)' }}>
        {task.refUrl ? (
          <>
            <button onClick={() => setRefOpen(v => !v)}
              style={{
                width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
              <span style={{ fontSize: 12, color: 'var(--c-text-tertiary)' }}>参考截图</span>
              <ChevronDown size={14} color="var(--c-text-tertiary)"
                style={{ transform: refOpen ? 'rotate(180deg)' : 'none', transition: '.18s' }} />
            </button>
            {refOpen && (
              <img
                src={task.refUrl}
                alt="参考截图"
                style={{ width: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 8, marginTop: 8, background: 'var(--c-bg)' }}
                onError={e => { e.currentTarget.style.display = 'none'; }}
              />
            )}
          </>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', display: 'flex', alignItems: 'center', gap: 5 }}>
            <ImageOff size={13} color="var(--c-text-tertiary)" />
            未关联参考截图 · 号码 {task.phone}
          </div>
        )}
      </div>

      {/* 备注 / 审核批注 */}
      {(task.note || task.review_note) && (
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--c-divider)' }}>
          {task.note && (
            <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: task.review_note ? 6 : 0 }}>
              📋 {task.note}
            </div>
          )}
          {task.review_note && (
            <div style={{ fontSize: 12, color: task.status === 'rejected' ? 'var(--c-danger)' : 'var(--c-text-secondary)' }}>
              {task.status === 'rejected' ? '❌ 未通过原因：' : '✅ 批注：'}{task.review_note}
            </div>
          )}
        </div>
      )}

      {/* 操作区 */}
      <div style={{ padding: '12px 14px' }}>
        {task.status === 'pending' && (
          <UploadButton taskId={task.id} label="上传审查视频" onSuccess={onUpload} />
        )}
        {task.status === 'rejected' && (
          <UploadButton taskId={task.id} label="重新上传视频" onSuccess={onUpload} accent="var(--c-danger)" />
        )}
        {task.status === 'submitted' && (
          <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', textAlign: 'center', padding: '4px 0' }}>
            已提交，等待管理员审核…
          </div>
        )}
        {task.status === 'approved' && (
          <div style={{ fontSize: 13, color: 'var(--c-success)', textAlign: 'center',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '4px 0' }}>
            <CheckCircle2 size={14} strokeWidth={2.5} /> 审核已通过
          </div>
        )}
        <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 8, textAlign: 'right' }}>
          指派于 {formatBJTime(task.created_at)}
        </div>
      </div>
    </div>
  );
}

// ── 上传按钮（含进度 + 选择来源弹层） ───────────────────────────────────────
// 移动端 accept="video/*" 在部分 Android 浏览器/微信中会直接打开相机，
// 通过底部弹层让用户明确选择「拍摄视频」或「从相册/文件选择」。
function UploadButton({ taskId, label, onSuccess, accent }) {
  const { showToast } = useToast();
  const cameraRef  = useRef(null); // capture="environment"
  const galleryRef = useRef(null); // 不带 capture，走系统文件选择器
  const [progress, setProgress] = useState(null); // 0-100 | null
  const [showSheet, setShowSheet] = useState(false);

  const doUpload = async (file, resetInput) => {
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      showToast('请选择视频文件', 'error'); return;
    }
    if (file.size > 80 * 1024 * 1024) {
      showToast('视频不能超过 80 MB', 'error'); return;
    }
    const formData = new FormData();
    formData.append('video', file);
    setProgress(0);
    try {
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `/api/video-review/${taskId}/upload`);
        const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setProgress(Math.round(ev.loaded / ev.total * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else {
            try { reject(new Error(JSON.parse(xhr.responseText).error || '上传失败')); }
            catch { reject(new Error('上传失败')); }
          }
        };
        xhr.onerror = () => reject(new Error('网络错误'));
        xhr.send(formData);
      });
      showToast('视频已提交，等待审核', 'success');
      onSuccess();
    } catch (err) {
      showToast(err.message || '上传失败', 'error');
    } finally {
      setProgress(null);
      resetInput();
    }
  };

  if (progress !== null) {
    // 传输完成后服务端还要做压缩处理，此时进度条停在100%但响应未返回，
    // 用「处理中」文案代替卡住的百分比，避免用户误以为卡死
    const finalizing = progress >= 100;
    return (
      <div style={{ padding: '10px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 6 }}>
          <span>{finalizing ? '处理中…' : '上传中…'}</span><span>{finalizing ? '' : `${progress}%`}</span>
        </div>
        <div style={{ height: 6, background: 'var(--c-bg)', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, background: accent || 'var(--c-primary)', borderRadius: 4, transition: 'width 0.2s',
            ...(finalizing ? { animation: 'pulse 1.2s ease-in-out infinite' } : {}) }} />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* 拍摄：capture="environment" 强制调用后置摄像头 */}
      <input ref={cameraRef} type="file" accept="video/*" capture="environment"
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; doUpload(f, () => { e.target.value = ''; }); }} />
      {/* 相册/文件：不带 capture，系统弹出完整选择器 */}
      <input ref={galleryRef} type="file" accept="video/mp4,video/quicktime,video/x-m4v,video/*"
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; doUpload(f, () => { e.target.value = ''; }); }} />

      <button
        className="btn btn-primary"
        style={{
          width: '100%', height: 44, borderRadius: 12, fontSize: 14, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          ...(accent ? { background: accent, borderColor: accent } : {}),
        }}
        onClick={() => setShowSheet(true)}>
        <Upload size={16} /> {label}
      </button>

      {/* 来源选择底部弹层 */}
      {showSheet && (
        <div
          onClick={() => setShowSheet(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            zIndex: 9000, display: 'flex', alignItems: 'flex-end',
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', background: 'var(--c-surface)', borderRadius: '18px 18px 0 0',
              padding: '20px 16px 32px', display: 'flex', flexDirection: 'column', gap: 10,
            }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--c-text)', textAlign: 'center', marginBottom: 4 }}>
              选择视频来源
            </div>
            <button
              style={{
                height: 50, borderRadius: 12, border: 'none', cursor: 'pointer',
                background: 'var(--c-bg)', fontSize: 15, fontWeight: 600, color: 'var(--c-text)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              }}
              onClick={() => { setShowSheet(false); setTimeout(() => cameraRef.current?.click(), 80); }}>
              <Video size={18} color="var(--c-primary)" /> 拍摄视频
            </button>
            <button
              style={{
                height: 50, borderRadius: 12, border: 'none', cursor: 'pointer',
                background: 'var(--c-bg)', fontSize: 15, fontWeight: 600, color: 'var(--c-text)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              }}
              onClick={() => { setShowSheet(false); setTimeout(() => galleryRef.current?.click(), 80); }}>
              <Upload size={18} color="var(--c-primary)" /> 从相册 / 文件选择
            </button>
            <button
              style={{
                height: 44, borderRadius: 12, border: 'none', cursor: 'pointer',
                background: 'transparent', fontSize: 14, color: 'var(--c-text-secondary)',
              }}
              onClick={() => setShowSheet(false)}>
              取消
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ── 主页面 ────────────────────────────────────────────────────────────────────
export default function VideoReviewUserPage() {
  const navigate    = useNavigate();
  const { showToast } = useToast();
  const { subscribe } = useSocket();
  const [tasks, setTasks]       = useState([]);
  const [loading, setLoading]   = useState(true);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/video-review/mine');
      setTasks(res.data.tasks || []);
    } catch {
      showToast('加载失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // 实时推送：管理员指派任务或审核结果变化时，自动刷新列表
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event, payload) => {
      if (event === 'push_message' && payload?.type === 'video_review') {
        fetchTasks();
      }
    });
  }, [subscribe, fetchTasks]);

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0',
            display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
          <Video size={16} /> 二次审查任务
        </span>
        <button onClick={fetchTasks}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0 4px 8px',
            display: 'flex', alignItems: 'center', color: 'var(--c-text-secondary)' }}>
          <RefreshCw size={17} />
        </button>
      </div>

      <div style={{ padding: '12px 16px', paddingBottom: 80 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--c-text-tertiary)' }}>
            <Loader size={28} style={{ animation: 'spin 1s linear infinite', display: 'block', margin: '0 auto 8px' }} />
            加载中…
          </div>
        ) : tasks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--c-text-tertiary)' }}>
            <Video size={36} style={{ display: 'block', margin: '0 auto 12px', opacity: 0.3 }} />
            <div style={{ fontSize: 15, fontWeight: 600 }}>暂无审查任务</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>管理员指派后会在这里显示</div>
          </div>
        ) : (
          tasks.map(t => <TaskCard key={t.id} task={t} onUpload={fetchTasks} />)
        )}
      </div>

      <TabBar mode="user" />
    </div>
  );
}
